import React, { FormEvent } from "react";
import { Package, Plus } from "lucide-react";
import { IdGenerator } from "src/utils";
import {
  CreateTransferOrderFormData,
  CreateTransferOrderFormDataKey,
  CreateTransferOrderFormAction,
  CreateTOProductInputField
} from "src/types";
import { ValidationErrors } from "src/errors";
import { useScrollOnMessage } from "src/hooks";
import { CreateTOProductInputTable } from "src/components/tables/inputTables";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { SubmissionTooltip } from "src/components/tooltips";

interface CreateTransferOrderFormProps {
  formData: CreateTransferOrderFormData;
  validationErrors?: ValidationErrors<CreateTransferOrderFormDataKey>;
  formLvlErrorMessage?: string;
  isFormComplete: boolean;
  handleInputChange: (
    field: CreateTransferOrderFormDataKey,
    value: CreateTransferOrderFormData[keyof CreateTransferOrderFormData]
  ) => void;
  handleSubmit: (e?: FormEvent) => Promise<void>;
  setAction: (action: CreateTransferOrderFormAction) => void;
}

const CreateTransferOrderForm: React.FC<CreateTransferOrderFormProps> = ({
  formData,
  validationErrors,
  formLvlErrorMessage,
  isFormComplete,
  handleInputChange,
  handleSubmit,
  setAction
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  const handleSetProducts: React.Dispatch<React.SetStateAction<CreateTOProductInputField[]>> = (updatedProducts) => {
    handleInputChange("products", updatedProducts as CreateTOProductInputField[]);
  };

  const handleAddNewProduct = () => {
    const newProduct: CreateTOProductInputField = {
      fieldId: IdGenerator.generateUniqueElementId(),
      productId: "",
      euQuantity: "",
      extraInfo: ""
    };
    handleSetProducts([...formData.products, newProduct]);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Create Transfer Order</h1>
            <p className="text-gray-300 mt-2 text-lg">Create a new transfer order manually</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-6">
              {/* Products Section */}
              <div className="border border-neutral-200 rounded-2xl p-6 bg-white">
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-black tracking-wide uppercase flex items-center">
                    <Package className="w-4 h-4 mr-2" />
                    Products
                  </h3>
                  <p className="text-xs text-neutral-600 mt-1">
                    Add products by entering their Product ID (6-15 digits) with EU quantity.
                  </p>
                </div>

                {formData.products.length > 0 ? (
                  <>
                    <CreateTOProductInputTable products={formData.products} setProducts={handleSetProducts} />
                    <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("products")} />
                  </>
                ) : (
                  <div className="bg-neutral-50 border border-dashed border-neutral-300 rounded-lg p-8 text-center">
                    <Package className="w-12 h-12 mx-auto mb-3 text-neutral-400" />
                    <p className="text-sm text-neutral-600 mb-4">No products added yet</p>
                    <p className="text-xs text-neutral-500">Click the button below to add products to this TO</p>
                  </div>
                )}

                <div className="mt-4">
                  <button
                    className="flex items-center space-x-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium shadow-sm"
                    onClick={handleAddNewProduct}
                    type="button"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Product</span>
                  </button>
                </div>
              </div>

              {/* Form Level Error */}
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

              {/* Submit Button */}
              <div className="flex items-center gap-4">
                <button
                  className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                  disabled={!isFormComplete}
                  onClick={() => setAction("CREATE")}
                  type="submit"
                >
                  Create Transfer Order
                </button>

                {!isFormComplete && <SubmissionTooltip formState="INCOMPLETE" />}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { CreateTransferOrderForm };
